import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, translations } from './translations';
import { useLocation, useNavigate } from 'react-router-dom';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get language from URL parameter, localStorage, or default to 'de'
  const getInitialLanguage = (): Language => {
    const searchParams = new URLSearchParams(location.search);
    const urlLang = searchParams.get('lang') as Language;
    if (urlLang && Object.keys(translations).includes(urlLang)) {
      return urlLang;
    }
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'de';
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  // Update URL and localStorage when language changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', language);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    localStorage.setItem('language', language);
  }, [language, location.pathname, navigate]);

  // Update language when URL parameter changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlLang = searchParams.get('lang') as Language;
    if (urlLang && Object.keys(translations).includes(urlLang) && urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.search]);

  const t = (key: string, params?: Record<string, string>): string => {
    let text = translations[language][key] || key;
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        text = text.replace(`{${key}}`, value);
      });
    }
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 